import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { CheckCircle2, XCircle, Ban, Bell, Sparkles, ChevronsRight, X, Volume2, VolumeX } from "lucide-react";
import useAviStream from "@/hooks/useAviStream";
import { useAuth } from "@/context/AuthContext";
import { playAviChime, playAviMessageChime, isChimeEnabled, setChimeEnabled } from "@/lib/aviChime";
import { isSoundAllowed, loadSoundPrefs } from "@/lib/soundPrefs";

/**
 * AviRealtimeOverlay — overlay flottant centré (§rev-avi-3 spec §7).
 *
 * S'abonne aux évts SSE et affiche une pastille pop centrée qui rebondit
 * (0.4 s). Si plusieurs événements arrivent en rafale, la file d'attente
 * est visible sous la forme d'un badge `+N autres en attente` — cliquer
 * les fait défiler un par un.
 *
 * L'overlay peut être replié en une pill discrète (coin haut-droit) via
 * le bouton `Réduire` — persistant en localStorage.
 */
const KIND_STYLE = {
  created:   { color: "#EC4899", Icon: Sparkles,     verb: "Nouvelle demande" },
  decision:  { color: "#4ADE80", Icon: CheckCircle2, verb: "Décision" },
  cancelled: { color: "#9CA3AF", Icon: Ban,          verb: "Annulation" },
  reminder:  { color: "#EC4899", Icon: Bell,         verb: "Relance" },
};

const PILL_KEY = "avi-overlay-collapsed";

function relativeTime(iso) {
  try {
    const t = new Date(iso).getTime();
    const s = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (s < 60) return `il y a ${s}s`;
    const m = Math.floor(s / 60);
    if (m < 60) return `il y a ${m} min`;
    const h = Math.floor(m / 60);
    return `il y a ${h}h`;
  } catch { return ""; }
}

export default function AviRealtimeOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(PILL_KEY) === "1"; } catch { return false; }
  });
  const [soundOn, setSoundOn] = useState(() => isChimeEnabled());
  useEffect(() => { if (user) loadSoundPrefs(); }, [user]);
  const dismissTimeoutRef = useRef(null);

  const push = useCallback((evt) => {
    // Ignore self-triggered events for the actor to éviter les rebonds de son propre clic.
    if (evt && evt.actor_id && evt.actor_id === user?.id) return;
    // Chime uniquement sur événements critiques : décision (approuvée /
    // refusée) et annulation. Pas de son sur créations ou relances.
    const isCritical =
      (evt?.kind === "decision" && (evt?.status === "approuvee" || evt?.status === "refusee")) ||
      evt?.kind === "cancelled";
    // Spec iter269 — gate central (Paramètres → Son des notifications) en
    // plus du mute local de l'overlay (isChimeEnabled dans playAviChime).
    const centralKey = evt?.kind === "cancelled" ? "avi_cancelled" : "avi_decision";
    if (isCritical && isSoundAllowed(centralKey)) {
      try { playAviChime(); } catch { /* silent */ }
    }
    setQueue((q) => [...q, evt].slice(-8));
  }, [user?.id]);

  useAviStream({
    onCreated:   push,
    onDecision:  push,
    onCancelled: push,
    onReminder:  push,
    // Point 2 rev post-prod-3 — les événements `message` déclenchent un
    // chime « discussion » distinct + un signal global pour rafraîchir le
    // badge bleu du sidebar. Aucun pop overlay (trop bruyant).
    onAny: (evt) => {
      if (!evt || evt.kind !== "message") return;
      if (evt.actor_id && evt.actor_id === user?.id) return;
      if (isSoundAllowed("avi_message")) { try { playAviMessageChime(); } catch {} }
      try { window.dispatchEvent(new Event("avi:msg-pulse")); } catch {}
    },
  }, { enabled: !!user });

  const current = queue[0];

  // Auto-dismiss après 6s si l'utilisateur ne fait rien (garde-fou).
  useEffect(() => {
    if (!current) return undefined;
    if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current);
    dismissTimeoutRef.current = setTimeout(() => {
      setQueue((q) => q.slice(1));
    }, 6000);
    return () => { if (dismissTimeoutRef.current) clearTimeout(dismissTimeoutRef.current); };
  }, [current?.at, current?.avi_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = () => setQueue((q) => q.slice(1));
  const dismissAll = () => setQueue([]);
  const goToAvi = () => {
    if (!current) return;
    navigate(`/avi/${current.avi_id}`);
    setQueue((q) => q.slice(1));
  };
  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const nv = !v;
      try { localStorage.setItem(PILL_KEY, nv ? "1" : "0"); } catch {}
      return nv;
    });
  };
  const toggleSound = () => {
    setSoundOn((v) => {
      const nv = !v;
      setChimeEnabled(nv);
      if (nv) {
        // Petit chime de confirmation pour prouver que le son marche
        // (bypass du gate car on vient de le réactiver).
        try { playAviChime({ force: true, volume: 0.05 }); } catch {}
      }
      return nv;
    });
  };

  if (!user) return null;

  // Mode replié : petite pill dans le coin haut-droit avec compteur.
  if (collapsed) {
    return (
      <div className="fixed top-4 right-4 z-40 flex items-center gap-2" data-testid="avi-overlay-collapsed">
        <button
          type="button"
          onClick={toggleSound}
          className="inline-flex items-center justify-center w-8 h-8 rounded-full transition-all"
          style={{
            background: "#161616",
            border: `1px solid ${soundOn ? "#EC4899" : "#383838"}`,
            color: soundOn ? "#F472B6" : "#6B7280",
          }}
          data-testid="avi-overlay-sound-toggle"
          title={soundOn ? "Couper le son des décisions critiques" : "Activer le son des décisions critiques"}
        >
          {soundOn ? <Volume2 size={13} /> : <VolumeX size={13} />}
        </button>
        <button
          type="button"
          onClick={toggleCollapsed}
          className="inline-flex items-center gap-2 h-8 px-3 rounded-full text-[11px] font-semibold transition-all"
          style={{
            background: "#161616", border: "1px solid #EC4899",
            color: "#F472B6", boxShadow: "0 4px 14px rgba(236,72,153,0.35)",
          }}
          data-testid="avi-overlay-pill"
          title="Rétablir les notifications AVI"
        >
          <Bell size={12} />
          AVI temps réel {queue.length > 0 && <span className="ml-1 rounded-full px-1.5 py-0.5" style={{ background: "#EC4899", color: "white" }}>{queue.length}</span>}
        </button>
      </div>
    );
  }

  if (!current) return null;

  const st = KIND_STYLE[current.kind] || KIND_STYLE.created;
  const Icon = st.Icon;
  const extras = queue.length - 1;
  const decisionLabel = current.kind === "decision"
    ? (current.decision === "approuvee" ? "approuvée" : current.decision === "refusee" ? "refusée" : "mise à jour")
    : null;

  return (
    <div
      className="fixed left-1/2 top-8 z-50 -translate-x-1/2 avi-overlay-pop"
      style={{ pointerEvents: "auto" }}
      data-testid="avi-overlay"
    >
      <div
        className="rounded-2xl p-4 pr-3 flex items-start gap-3 min-w-[420px] max-w-[560px]"
        style={{
          background: "#161616",
          border: `2px solid ${st.color}`,
          boxShadow: `0 12px 32px rgba(0,0,0,0.55), 0 0 24px ${st.color}44`,
        }}
      >
        <div
          className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${st.color}22`, border: `1px solid ${st.color}` }}
        >
          <Icon size={18} style={{ color: st.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: st.color }}>
              {st.verb}
            </span>
            <span className="font-mono text-[11px] text-muted-foreground">{current.reference}</span>
            {decisionLabel && (
              <span className="text-[10px] uppercase tracking-widest" style={{ color: current.decision === "refusee" ? "#F87171" : "#4ADE80" }}>
                · {decisionLabel}
              </span>
            )}
          </div>
          <div className="text-[13px] font-semibold mt-0.5 truncate" title={current.subject}>
            {current.subject || "—"}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">{relativeTime(current.at)}</div>
          <div className="flex items-center gap-2 mt-2.5">
            <button
              type="button"
              onClick={goToAvi}
              className="inline-flex items-center gap-1 h-7 px-3 rounded-lg text-[11px] font-semibold text-white transition-all"
              style={{ background: st.color }}
              data-testid="avi-overlay-open"
            >
              Ouvrir <ChevronsRight size={11} />
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="h-7 px-2.5 rounded-lg text-[11px] font-semibold text-muted-foreground hover:text-white"
              data-testid="avi-overlay-dismiss"
            >
              Ignorer
            </button>
            {extras > 0 && (
              <button
                type="button"
                onClick={dismissAll}
                className="h-7 px-2.5 rounded-lg text-[10px] font-semibold"
                style={{ background: "#212121", border: "1px solid #383838", color: "#9CA3AF" }}
                data-testid="avi-overlay-queue"
                title="Ignorer toute la file"
              >
                +{extras} autres en attente
              </button>
            )}
          </div>
        </div>
        <div className="flex flex-col items-center gap-1 ml-1 flex-shrink-0">
          <button
            type="button"
            onClick={toggleCollapsed}
            className="text-muted-foreground hover:text-white"
            data-testid="avi-overlay-collapse"
            aria-label="Réduire"
            title="Réduire"
          >
            <X size={14} />
          </button>
          <button
            type="button"
            onClick={toggleSound}
            className="text-muted-foreground hover:text-white"
            data-testid="avi-overlay-sound-toggle"
            aria-label={soundOn ? "Couper le son" : "Activer le son"}
            title={soundOn ? "Son activé — cliquez pour couper" : "Son coupé — cliquez pour activer"}
          >
            {soundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
}
